import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Zap, Monitor, Smartphone, RefreshCw, TrendingUp, AlertTriangle, CheckCircle, Clock, ExternalLink } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface PageSpeedMetrics {
  performance: number;
  accessibility: number;
  bestPractices: number;
  seo: number;
  firstContentfulPaint: number;
  largestContentfulPaint: number;
  firstInputDelay: number;
  cumulativeLayoutShift: number;
  speedIndex: number;
  totalBlockingTime: number;
}

interface PageSpeedReport {
  url: string;
  strategy: 'mobile' | 'desktop';
  timestamp: number;
  scores: PageSpeedMetrics;
  opportunities: Array<{
    title: string;
    description: string;
    impact: 'low' | 'medium' | 'high';
    savings: string;
  }>;
  diagnostics: Array<{
    title: string;
    description: string;
    value: string;
    status: 'pass' | 'warning' | 'fail';
  }>;
  loadingExperience: {
    fcpPercentile: number;
    lcpPercentile: number;
    fidPercentile: number;
    clsPercentile: number;
  };
}

const PageSpeedTester: React.FC = () => {
  const [url, setUrl] = useState('');
  const [strategy, setStrategy] = useState<'mobile' | 'desktop'>('mobile');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [report, setReport] = useState<PageSpeedReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useSEO({
    title: 'Page Speed Tester - Website Performance Analyzer',
    description: 'Test website speed and performance using Google PageSpeed insights. Analyze Core Web Vitals, get optimization suggestions.',
    keywords: 'page speed test, website performance, pagespeed insights, core web vitals, site speed, performance optimization',
    canonical: 'https://snap-tools.online/seo/page-speed-tester'
  });

  // Analyze page speed
  const analyzePageSpeed = async () => {
    if (!url) {
      toast.error('Please enter a URL to analyze');
      return;
    }

    // Validate URL
    let testUrl = url;
    try {
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        testUrl = 'https://' + url;
      }
      new URL(testUrl);
    } catch (error) {
      toast.error('Please enter a valid URL');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setReport(null);

    try {
      // In a real implementation, you would call the PageSpeed Insights API
      // For demo purposes, we'll simulate the analysis
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Mock PageSpeed data
      const mockReport: PageSpeedReport = {
        url: testUrl,
        strategy,
        timestamp: Date.now(),
        scores: {
          performance: Math.floor(Math.random() * 40) + 60, // 60-99
          accessibility: Math.floor(Math.random() * 30) + 70, // 70-99
          bestPractices: Math.floor(Math.random() * 25) + 75, // 75-99
          seo: Math.floor(Math.random() * 20) + 80, // 80-99
          firstContentfulPaint: Math.random() * 2 + 1, // 1-3 seconds
          largestContentfulPaint: Math.random() * 3 + 2, // 2-5 seconds
          firstInputDelay: Math.random() * 200 + 50, // 50-250ms
          cumulativeLayoutShift: Math.random() * 0.2, // 0-0.2
          speedIndex: Math.random() * 3 + 2, // 2-5 seconds
          totalBlockingTime: Math.random() * 400 + 100 // 100-500ms
        },
        opportunities: [
          {
            title: 'Serve images in next-gen formats',
            description: 'Image formats like WebP and AVIF often provide better compression than PNG or JPEG.',
            impact: 'high',
            savings: '1.2s'
          },
          {
            title: 'Eliminate render-blocking resources',
            description: 'Resources are blocking the first paint of your page.',
            impact: 'medium',
            savings: '0.8s'
          },
          {
            title: 'Reduce unused CSS',
            description: 'Reduce unused rules from stylesheets and defer CSS not used for above-the-fold content.',
            impact: 'medium',
            savings: '0.6s'
          },
          {
            title: 'Minify JavaScript',
            description: 'Minifying JavaScript files can reduce payload sizes and script parse time.',
            impact: 'low',
            savings: '0.3s'
          }
        ],
        diagnostics: [
          {
            title: 'Avoid multiple page redirects',
            description: 'Redirects introduce additional delays before the page can be loaded.',
            value: '1 redirect',
            status: 'warning'
          },
          {
            title: 'Uses text compression',
            description: 'Text-based resources should be served with compression.',
            value: 'Enabled',
            status: 'pass'
          },
          {
            title: 'Avoid enormous network payloads',
            description: 'Large network payloads cost users real money.',
            value: '2.1 MB',
            status: 'warning'
          }
        ],
        loadingExperience: {
          fcpPercentile: Math.random() * 2 + 1,
          lcpPercentile: Math.random() * 3 + 2,
          fidPercentile: Math.random() * 200 + 50,
          clsPercentile: Math.random() * 0.2
        }
      };

      setReport(mockReport);
      toast.success('Page speed analysis completed');
    } catch (error) {
      console.error('Analysis failed:', error);
      setError('Failed to analyze page speed. Please check the URL and try again.');
      toast.error('Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Load example
  const loadExample = () => {
    setUrl('https://web.dev');
    toast.success('Example URL loaded');
  };

  // Get score color
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Get score background color
  const getScoreBgColor = (score: number) => {
    if (score >= 90) return 'bg-green-100';
    if (score >= 50) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  // Get Core Web Vitals status
  const getCWVStatus = (metric: string, value: number) => {
    const thresholds = {
      fcp: { good: 1.8, poor: 3.0 },
      lcp: { good: 2.5, poor: 4.0 },
      fid: { good: 100, poor: 300 },
      cls: { good: 0.1, poor: 0.25 }
    };

    const threshold = thresholds[metric as keyof typeof thresholds];
    if (!threshold) return 'unknown';

    if (value <= threshold.good) return 'good';
    if (value <= threshold.poor) return 'needs-improvement';
    return 'poor';
  };

  // Format metric value
  const formatMetricValue = (metric: string, value: number) => {
    switch (metric) {
      case 'fcp':
      case 'lcp':
      case 'speedIndex':
        return `${value.toFixed(1)}s`;
      case 'fid':
      case 'totalBlockingTime':
        return `${Math.round(value)}ms`;
      case 'cls':
        return value.toFixed(3);
      default:
        return value.toString();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Zap className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Page Speed Tester</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Analyze your website's performance using Google PageSpeed Insights. Get Core Web Vitals metrics and optimization recommendations.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* URL Input and Controls */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Analyze Website Performance</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && analyzePageSpeed()}
                />
                <button
                  onClick={loadExample}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Example
                </button>
              </div>

              <div className="flex gap-3 items-center">
                <div className="flex bg-white rounded-lg p-1">
                  <button
                    onClick={() => setStrategy('mobile')}
                    className={`flex items-center gap-2 py-2 px-4 rounded-md transition-colors ${
                      strategy === 'mobile' 
                        ? 'bg-purple-600 text-white' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Smartphone className="w-4 h-4" />
                    Mobile
                  </button>
                  <button
                    onClick={() => setStrategy('desktop')}
                    className={`flex items-center gap-2 py-2 px-4 rounded-md transition-colors ${
                      strategy === 'desktop' 
                        ? 'bg-purple-600 text-white' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Monitor className="w-4 h-4" />
                    Desktop
                  </button>
                </div>

                <button
                  onClick={analyzePageSpeed}
                  disabled={isAnalyzing || !url}
                  className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isAnalyzing ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Zap className="w-4 h-4" />
                  )}
                  {isAnalyzing ? 'Analyzing...' : 'Analyze'}
                </button>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600" />
                <div>
                  <h3 className="font-semibold text-red-900">Analysis Failed</h3>
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          {report && (
            <div className="space-y-6 mb-8">
              {/* Overall Scores */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold">Performance Scores</h3>
                    <p className="text-gray-600">
                      {report.url} • {report.strategy} • {new Date(report.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {strategy === 'mobile' ? (
                      <Smartphone className="w-5 h-5 text-gray-600" />
                    ) : (
                      <Monitor className="w-5 h-5 text-gray-600" />
                    )}
                    <span className="text-sm text-gray-600 capitalize">{strategy}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Performance', value: report.scores.performance, icon: TrendingUp },
                    { label: 'Accessibility', value: report.scores.accessibility, icon: CheckCircle },
                    { label: 'Best Practices', value: report.scores.bestPractices, icon: CheckCircle },
                    { label: 'SEO', value: report.scores.seo, icon: CheckCircle }
                  ].map((score, index) => (
                    <div key={index} className={`${getScoreBgColor(score.value)} rounded-lg p-4`}>
                      <div className="flex items-center justify-between mb-2">
                        <score.icon className={`w-5 h-5 ${getScoreColor(score.value)}`} />
                        <div className={`text-2xl font-bold ${getScoreColor(score.value)}`}>
                          {score.value}
                        </div>
                      </div>
                      <div className="text-sm font-medium text-gray-700">{score.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Core Web Vitals */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h4 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Core Web Vitals
                </h4>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { 
                      label: 'First Contentful Paint', 
                      value: report.scores.firstContentfulPaint,
                      metric: 'fcp',
                      description: 'Time until first content appears'
                    },
                    { 
                      label: 'Largest Contentful Paint', 
                      value: report.scores.largestContentfulPaint,
                      metric: 'lcp',
                      description: 'Time until largest content appears'
                    },
                    { 
                      label: 'First Input Delay', 
                      value: report.scores.firstInputDelay,
                      metric: 'fid',
                      description: 'Time until page responds to input'
                    },
                    { 
                      label: 'Cumulative Layout Shift', 
                      value: report.scores.cumulativeLayoutShift,
                      metric: 'cls',
                      description: 'Visual stability of the page'
                    }
                  ].map((metric, index) => {
                    const status = getCWVStatus(metric.metric, metric.value);
                    const statusColors: Record<string, string> = {
                      good: 'bg-green-100 text-green-800',
                      'needs-improvement': 'bg-yellow-100 text-yellow-800',
                      poor: 'bg-red-100 text-red-800',
                      unknown: 'bg-gray-100 text-gray-800'
                    };
                    const statusColor = statusColors[status] || 'bg-gray-100 text-gray-800';

                    return (
                      <div key={index} className="bg-white rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-lg font-bold">
                            {formatMetricValue(metric.metric, metric.value)}
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                            {status.replace('-', ' ').toUpperCase()}
                          </span>
                        </div>
                        <div className="text-sm font-medium text-gray-900 mb-1">
                          {metric.label}
                        </div>
                        <div className="text-xs text-gray-600">
                          {metric.description}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Opportunities */}
              <div className="bg-orange-50 rounded-lg p-6">
                <h4 className="font-semibold text-orange-900 mb-4">Optimization Opportunities</h4>
                <div className="space-y-3">
                  {report.opportunities.map((opportunity, index) => (
                    <div key={index} className="bg-white rounded-lg p-4 border-l-4 border-orange-400">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{opportunity.title}</span>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            opportunity.impact === 'high' ? 'bg-red-100 text-red-800' :
                            opportunity.impact === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {opportunity.impact.toUpperCase()}
                          </span>
                          <span className="text-sm font-bold text-green-600">
                            Save {opportunity.savings}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">{opportunity.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Diagnostics */}
              <div className="bg-purple-50 rounded-lg p-6">
                <h4 className="font-semibold text-purple-900 mb-4">Diagnostics</h4>
                <div className="space-y-3">
                  {report.diagnostics.map((diagnostic, index) => (
                    <div key={index} className="bg-white rounded-lg p-4 flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{diagnostic.title}</div>
                        <div className="text-sm text-gray-600">{diagnostic.description}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium">{diagnostic.value}</span>
                        <div className={`w-3 h-3 rounded-full ${
                          diagnostic.status === 'pass' ? 'bg-green-400' :
                          diagnostic.status === 'warning' ? 'bg-yellow-400' :
                          'bg-red-400'
                        }`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">Core Web Vitals Explained</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>LCP (Largest Contentful Paint):</strong> Loading performance</li>
                <li>• <strong>FID (First Input Delay):</strong> Interactivity responsiveness</li>
                <li>• <strong>CLS (Cumulative Layout Shift):</strong> Visual stability</li>
                <li>• <strong>FCP (First Contentful Paint):</strong> Initial loading speed</li>
                <li>• <strong>Speed Index:</strong> How quickly content is visually displayed</li>
                <li>• <strong>TBT (Total Blocking Time):</strong> Main thread blocking time</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Performance Optimization Tips</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• <strong>Image Optimization:</strong> Use WebP, compress images, lazy loading</li>
                <li>• <strong>Code Splitting:</strong> Load only necessary JavaScript</li>
                <li>• <strong>Critical CSS:</strong> Inline above-the-fold styles</li>
                <li>• <strong>Resource Hints:</strong> Use preload, prefetch, preconnect</li>
                <li>• <strong>CDN Usage:</strong> Serve content from edge locations</li>
                <li>• <strong>Caching:</strong> Implement proper caching strategies</li>
              </ul>
            </div>

            <div className="bg-yellow-50 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 mb-3">Performance Testing Tools</h4>
              <div className="space-y-3">
                <a
                  href="https://pagespeed.web.dev/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-yellow-700 hover:text-yellow-800"
                >
                  <ExternalLink className="w-4 h-4" />
                  PageSpeed Insights
                </a>
                <a
                  href="https://web.dev/measure/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-yellow-700 hover:text-yellow-800"
                >
                  <ExternalLink className="w-4 h-4" />
                  Web.dev Measure
                </a>
                <a
                  href="https://www.webpagetest.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-yellow-700 hover:text-yellow-800"
                >
                  <ExternalLink className="w-4 h-4" />
                  WebPageTest
                </a>
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="font-semibold text-purple-900 mb-3">Performance Budgets</h4>
              <ul className="text-sm text-purple-800 space-y-2">
                <li>• <strong>LCP:</strong> Good ≤ 2.5s, Poor ≥ 4.0s</li>
                <li>• <strong>FID:</strong> Good ≤ 100ms, Poor ≥ 300ms</li>
                <li>• <strong>CLS:</strong> Good ≤ 0.1, Poor ≥ 0.25</li>
                <li>• <strong>FCP:</strong> Good ≤ 1.8s, Poor ≥ 3.0s</li>
                <li>• <strong>Performance Score:</strong> 90+ Excellent, 50+ Average</li>
                <li>• <strong>Page Size:</strong> Keep under 1MB for mobile</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PageSpeedTester;
